import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import { useTranslation } from "../../../../../../../hooks/useTranslation";
import FormTextInput from "../../../../../../../components/common/PL/TextInput/FormTextInput";
import FormSelect from "../../../../../../../components/common/PL/Select/FormSelect";
import useVehicleBrands from "../../../../../../../hooks/useVehicleBrands";
import useVehicleTypes from "../../../../../../../hooks/useVehicleTypes";
import useInsuranceCompanies from "../../../../../../../hooks/useInsuranceCompanies";
import useVehicleSearch, {
  MIN_PLATE_SEARCH_LENGTH,
} from "../../../../../../../hooks/useVehicleSearch";
import { FindVehicleResult } from "../../../../../../../passapp-sdk/vehicle";
import FormDocumentPreviewForm from "../../../../../../../components/common/PL/PicturePreview/FormDocumentPreviewForm";
import FormDatePicker from "../../../../../../../components/common/PL/DatePicker/FormDatePicker";
import Button from "../../../../../../../components/common/PL/Button/Button";
import DialogConfirmation from "../../../../../../../components/common/PL/Dialog/DialogConfirmation";
import EmpresaSearch from "../../../AccessTypeForm/TransportContentForm/EmpresaSearch";
import BundleKey from "../../../../../../../translation/bundleKey";
import ExistingVehicleCard from "./ExistingVehicleCard";
import VehicleSummaryCard from "../VehicleSummaryCard";
import "./VehicleContentUserDataForm.css";
import { RegistrationMode } from "../../../../registroIngresoNavigation";
import {
  useTransportAuthPrefill,
  useTransportFieldLock,
} from "../../../AccessTypeForm/TransportContentForm/useTransportAuthPrefill";
import { validateNotExpired } from "../../../../utils/registroIngresoSchema";
import { parseApiDate, parseApiOrIsoDate } from "../../../../utils/apiDates";
import useEvent from "../../../../hooks/useEvent";
import { EntryRegistrationFormValues } from "../../../types";

const parseVtvExpirationDate = parseApiDate;

/** Las patentes se comparan sin espacios ni mayúsculas: "AB 123 CD" y "ab123cd" son la misma. */
const normalizePlate = (plate: unknown): string =>
  String(plate ?? "")
    .replace(/\s+/g, "")
    .toUpperCase();

const samePlate = (a: unknown, b: unknown): boolean => {
  const left = normalizePlate(a);
  return !!left && left === normalizePlate(b);
};

/** Mismo filtro que aplica el input de patente (`extraType="plate"` en TextInput) al tipear: sin
 * esto, la patente buscada pasaba tal cual —minúsculas incluidas— al alta de un vehículo nuevo. */
const toPlateFieldValue = (raw: string): string => raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

/** Todos los `reset` de esta pantalla conservan el estado de validación del formulario. */
const RESET_OPTIONS = {
  // Sin esto, `reset` tira las referencias de todos los campos: los que tienen input se vuelven a
  // registrar al renderizar, pero los que se registran a mano desde un efecto — `selectedVehicleId`
  // acá, `transportAuthorizationSelected` en el formulario de transporte — quedan fuera del form.
  // Su error de "campo obligatorio" sobrevive (`keepErrors`) y ya no hay validación que lo limpie:
  // el resumen seguía pidiendo la autorización con una ya seleccionada, y el submit no salía nunca.
  keepFieldsRef: true,
  keepErrors: true,
  keepDirty: true,
  keepTouched: true,
  keepIsSubmitted: true,
  keepSubmitCount: true,
} as const;

/** Contrapartida de `vehicleFormValues`: deja la sección en blanco. */
const EMPTY_VEHICLE_VALUES = {
  vehiclePlate: "",
  vehicleColor: "",
  vehicleBrand: "",
  vehicleModel: "",
  insuranceExpirationDate: null,
  insurancePhoto: null,
  vtvExpirationDate: null,
  vtvPhoto: null,
  vehicleTypeId: "",
  vehicleInsuranceCompany: "",
  vehiclePolicyNumber: "",
  vehicleAppliesVtv: true,
};

/** El vehículo recién dado de alta todavía no existe en `vehicles`: su única copia son estos
 * campos del formulario. Los guardamos aparte para que destildar la tarjeta — que limpia el
 * formulario — no se lleve puesto lo que la tarjeta muestra. */
const pickVehicleValues = (values: any) =>
  Object.keys(EMPTY_VEHICLE_VALUES).reduce(
    (acc, key) => ({ ...acc, [key]: values[key] }),
    {} as Record<string, any>
  );

/** Id que usa el vehículo dado de alta en esta pantalla hasta que el submit lo crea de verdad. */
const NEW_VEHICLE_ID = "new";

type VehicleContentUserDataFormProps = {
  isAccordion?: boolean;
  vehicles?: any[];
  disabled?: boolean;
  mode?: RegistrationMode;
};

const VehicleContentUserDataForm: React.FC<VehicleContentUserDataFormProps> = ({
  isAccordion = false,
  vehicles = [],
  disabled = false,
  mode = "entry",
}) => {
  const { t } = useTranslation();
  const {
    control,
    watch,
    setValue,
    trigger,
    getValues,
    register,
    unregister,
    reset,
    formState: { isSubmitted },
  } = useFormContext();
  const { brands: vehicleBrandOptions } = useVehicleBrands();
  const { vehicleTypes: vehicleTypeOptions } = useVehicleTypes();
  const { companies: insuranceCompanyOptions } = useInsuranceCompanies();
  const [showAddForm, setShowAddForm] = useState(false);
  const [isNewVehicleSaved, setIsNewVehicleSaved] = useState(false);
  const [newVehicleSnapshot, setNewVehicleSnapshot] = useState<Record<string, any> | null>(null);
  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [vehicleSelectError, setVehicleSelectError] = useState<string | null>(null);
  const { saveExistingVehicle } = useEvent();

  // Egreso ya conoce su vehículo de forma determinística (el del evento de entrada): no busca ni
  // llama selectVehicle. Sólo ingreso pasa por el flujo nuevo.
  const isExitMode = mode === "exit";
  // "Registrar salida sin ingreso previo" también usa mode="exit", pero no hay evento de entrada
  // del que sacar el vehículo: sin esta distinción quedaba con la lista fija de `getVehicles`
  // (todo el parque del chofer) y sin buscador, como si el vehículo ya estuviera resuelto.
  const entranceEvent = watch("entranceEvent");
  const isDeterministicExit = isExitMode && !!entranceEvent;

  React.useEffect(() => {
    if (isDeterministicExit) setShowAddForm(false);
  }, [isDeterministicExit]);

  // Prefill impuesto por la autorización de transporte seleccionada. Fuera del flujo de
  // transportistas siempre es null / false, así que nada de esto se activa.
  const authPrefill = useTransportAuthPrefill();
  const isLocked = useTransportFieldLock();
  const isPlateLocked = isLocked("vehiclePlate");

  const selectedVehicleId = watch("selectedVehicleId");
  const newVehiclePlate = watch("vehiclePlate");
  const newVehicleBrand = watch("vehicleBrand");
  const newVehicleColor = watch("vehicleColor");
  const newVehicleModel = watch("vehicleModel");
  const appliesVtv = watch("vehicleAppliesVtv");
  const selectedAccessMethod = watch("selectedAccessMethod");
  const selectedVehicleTypeId = watch("vehicleTypeId");
  const personId = watch("id");
  const vehicleSearch = watch("vehicleSearch");
  // La autorización de transporte fija la patente. Fuera de ese flujo `authPrefill` es siempre
  // null, así que `authPlate` queda vacío y nada de lo que depende de él se activa. Con el chofer
  // saliendo a pie la autorización no manda: imponer su patente acá volvería a meter el camión en
  // un egreso que el guardia marcó explícitamente sin vehículo.
  const exitOnFoot = watch("exitOnFoot");
  const authPlate = exitOnFoot ? "" : authPrefill?.vehiclePlate ?? "";
  const tractorTypeId = React.useMemo(
    () =>
      vehicleTypeOptions.find((option) => option.label.trim().toLowerCase().includes("tractor"))
        ?.value || "",
    [vehicleTypeOptions]
  );

  // Vehículos ya conocidos en esta sesión: en egreso siguen siendo los que precargó `getVehicles`
  // (el vehículo ya se sabe de antemano, no es un flujo de búsqueda); en ingreso es lo que fue
  // devolviendo `selectVehicle` a medida que el guardia elige filas del buscador.
  const associatedVehiclesValue = watch("associatedVehicles");
  const knownVehicles: any[] = isDeterministicExit ? vehicles : associatedVehiclesValue || [];

  const {
    results: vehicleSearchResults,
    loading: vehicleSearchLoading,
    searchError: vehicleSearchApiError,
    selecting: selectingVehicle,
    selectVehicle: selectVehicleApi,
    findExact: findExactVehicle,
  } = useVehicleSearch(isDeterministicExit ? null : personId, vehicleSearch || "");

  /** `selectVehicle` es autoritativo en cada llamada (puede traer cambios hechos desde la app del
   * usuario, o los que acabamos de guardar nosotros con `updateVehicle`): por eso esto reemplaza la
   * entrada existente en vez de conservar la primera copia que se vio en la sesión. */
  const appendKnownVehicle = React.useCallback(
    (vehicle: any) => {
      const current = (getValues("associatedVehicles") as any[]) || [];
      const index = current.findIndex((v: any) => v.id === vehicle.id);
      const next =
        index === -1
          ? [...current, vehicle]
          : current.map((v: any, i: number) => (i === index ? vehicle : v));
      setValue("associatedVehicles", next);
    },
    [getValues, setValue]
  );

  // Sólo las altas nuevas de transporte arrancan como Tractor. Las unidades existentes conservan
  // su tipo registrado, aun si se cargaron históricamente con otro valor.
  React.useEffect(() => {
    if (
      !isAccordion ||
      selectedAccessMethod !== "transport" ||
      !tractorTypeId ||
      selectedVehicleTypeId ||
      (selectedVehicleId && selectedVehicleId !== NEW_VEHICLE_ID)
    )
      return;
    setValue("vehicleTypeId", tractorTypeId);
  }, [
    isAccordion,
    selectedAccessMethod,
    tractorTypeId,
    selectedVehicleTypeId,
    selectedVehicleId,
    setValue,
  ]);

  const showAddFormRef = React.useRef(showAddForm);
  React.useEffect(() => {
    showAddFormRef.current = showAddForm;
    if (isSubmitted) {
      trigger("selectedVehicleId");
    }
  }, [showAddForm, trigger, isSubmitted]);

  // Register selectedVehicleId as required so it appears in form errors
  React.useEffect(() => {
    register("selectedVehicleId", {
      validate: (value) => {
        if (showAddFormRef.current) return true;
        if (value) return true;
        return "Debe seleccionar o agregar un vehículo";
      },
    });
    return () => {
      unregister("selectedVehicleId");
    };
  }, [register, unregister]);

  /** Campos del formulario que salen de un vehículo ya registrado. Los cuatro últimos sólo se usan
   * en el formulario de transportistas, pero completarlos siempre evita que queden colgados de un
   * vehículo anterior. */
  const vehicleFormValues = React.useCallback(
    (vehicle: any) => ({
      vehiclePlate: isPlateLocked ? authPlate : vehicle.plate || "",
      vehicleColor: vehicle.color || "",
      vehicleBrand: vehicle.brand || "",
      vehicleModel: vehicle.model || "",
      insuranceExpirationDate: parseApiOrIsoDate(vehicle.insuranceExpirationDate),
      insurancePhoto: vehicle.insurancePhoto || null,
      vtvExpirationDate: parseVtvExpirationDate(vehicle.technicalReviewExpirationDate),
      // `uploadVehicle` persiste la VTV como `technicalReviewPhoto1`. `vtvPhoto` sólo aparece en
      // algunos payloads legacy; leer únicamente ese alias hacía que la foto ya guardada pareciera
      // ausente cada vez que se autocompletaba el vehículo principal.
      vtvPhoto:
        vehicle.technicalReviewPhoto1 ?? vehicle.vtvPhoto ?? vehicle.technicalReviewPhoto ?? null,
      vehicleTypeId: vehicle.vehicleType || "",
      vehicleInsuranceCompany: vehicle.insuranceCompanyId || "",
      vehiclePolicyNumber: vehicle.insurancePolicy || "",
      vehicleAppliesVtv: !!vehicle.technicalReviewExpirationDate,
    }),
    [isPlateLocked, authPlate]
  );

  // Cubre el caso en que `selectedVehicleId` llega de afuera (p. ej. al restaurar una pestaña) sin
  // pasar por `handleSelectVehicle`/`handleEditVehicle`, que ya hidratan solos. No corre mientras
  // se está editando (`showAddForm`) para no pisar lo que el guardia está tipeando, ni cuando ya
  // hay algo cargado (`vehiclePlate`): `selectVehicle` es autoritativo en el momento de la
  // selección, pero una vez que el formulario tiene datos no hay que forzar otra hidratación en
  // cada render sólo porque `knownVehicles` cambió por otro motivo (p. ej. se guardó un acoplado).
  React.useEffect(() => {
    if (showAddForm) return;
    if (!selectedVehicleId || selectedVehicleId === NEW_VEHICLE_ID || !knownVehicles?.length) {
      return;
    }
    if (getValues("vehiclePlate")) return;

    const vehicle =
      knownVehicles.find((v: any) => v.id === selectedVehicleId) ||
      knownVehicles.find((v: any) => v.plate && v.plate === selectedVehicleId);
    if (!vehicle) return;

    reset({ ...getValues(), ...vehicleFormValues(vehicle) }, RESET_OPTIONS);
  }, [selectedVehicleId, knownVehicles, showAddForm, getValues, reset, vehicleFormValues]);

  const selectedVehicle = React.useMemo(
    () =>
      knownVehicles.find(
        (v: any) => v.id === selectedVehicleId || samePlate(v.plate, selectedVehicleId)
      ) ?? null,
    [knownVehicles, selectedVehicleId]
  );

  /** `id`/`checked` siguen el toggle de siempre; `vehicleOverride`, cuando ya tenemos el `Vehicle`
   * completo a mano (recién llegado de `selectVehicle`), evita depender de que `knownVehicles` ya
   * se haya actualizado en este mismo render.
   *
   * Siempre rehidrata con lo mejor que tengamos (`vehicleOverride` o `knownVehicles`): `selectVehicle`
   * se llama en cada selección y es autoritativo cada vez — puede traer cambios hechos desde la app
   * del usuario, o los que el propio guardia acaba de guardar. No hay que "recordar" que ya se
   * hidrató antes en la sesión y saltearse la actualización. */
  const handleSelectVehicle = (id: string, checked: boolean, vehicleOverride?: any) => {
    if (disabled) return;
    if (checked) {
      setShowAddForm(false);
      setEditingVehicleId(null);

      // El alta reciente no está en `knownVehicles`: sus datos vuelven del snapshot.
      const vehicle =
        vehicleOverride ||
        knownVehicles.find((v: any) => v.id === id) ||
        knownVehicles.find((v: any) => v.plate && v.plate === id);
      const restoredValues =
        id === NEW_VEHICLE_ID ? newVehicleSnapshot : vehicle && vehicleFormValues(vehicle);

      reset(
        {
          ...getValues(),
          selectedVehicleId: id,
          ...restoredValues,
        },
        RESET_OPTIONS
      );
    } else if (selectedVehicleId === id) {
      // Sólo se deselecciona: no se pisa con `EMPTY_VEHICLE_VALUES` por la misma razón que en el
      // efecto de arriba — lo que el guardia haya completado a mano en este vehículo (marca,
      // seguro, VTV, fotos) todavía no está persistido, y un destilde no debería hacerle perder ese
      // trabajo. Volver a tildarlo sigue mostrando lo mismo, no lo del backend.
      reset({ ...getValues(), selectedVehicleId: null }, RESET_OPTIONS);
    }
  };

  // Se llama sólo al pasar de la tarjeta ("VehicleSummaryCard") al formulario de edición — mientras
  // se edita, la tarjeta no se renderiza, así que no hay riesgo de que esto se dispare de nuevo y
  // pise una edición en curso. `vehicle` es `selectedVehicle` (la copia más fresca en
  // `knownVehicles`), así que siempre conviene hidratar con eso.
  const handleEditVehicle = (vehicle: any) => {
    if (disabled) return;
    setEditingVehicleId(vehicle.id);
    setShowAddForm(true);
    setIsNewVehicleSaved(false);

    reset(
      {
        ...getValues(),
        selectedVehicleId: vehicle.id,
        ...vehicleFormValues(vehicle),
      },
      RESET_OPTIONS
    );
  };

  // La autorización viene con una patente: si el guardia elige otro vehículo se lo avisamos, pero
  // puede seguir igual (p. ej. el camión se cambió sobre la marcha).
  const [plateMismatch, setPlateMismatch] = React.useState<string | null>(null);

  const handleSelectVehicleChecked = (vehicle: any) => {
    if (authPlate && !samePlate(vehicle.plate, authPlate)) {
      setPlateMismatch(vehicle.id);
      return;
    }
    // Un resultado de findVehicles ya trae la ficha completa después de selectVehicle:
    // abrirlo directamente permite revisarla o editarla antes de continuar con el ingreso.
    handleEditVehicle(vehicle);
  };

  const handleConfirmPlateMismatch = () => {
    if (plateMismatch) {
      const vehicle = knownVehicles.find((item: any) => item.id === plateMismatch);
      if (vehicle) handleEditVehicle(vehicle);
    }
    setPlateMismatch(null);
  };

  const handleCancelPlateMismatch = () => {
    setPlateMismatch(null);
    // Volvemos al vehículo de la autorización; si el chofer no lo tiene registrado, queda sin
    // selección y el alta se abre con la patente puesta, como al entrar.
    const authVehicle = knownVehicles.find((v: any) => samePlate(v.plate, authPlate));
    if (authVehicle?.id) handleSelectVehicle(authVehicle.id, true);
    else if (selectedVehicleId) handleSelectVehicle(selectedVehicleId, false);
  };

  // `searchText` llega desde el dropdown de búsqueda, pero si alguien engancha esto directo a un
  // `onClick` el primer argumento pasa a ser el evento de React y la patente terminaría mostrando
  // "[object Object]". Sólo aceptamos strings.
  const handleAddVehicle = (searchText: unknown = "") => {
    if (disabled) return;
    setShowAddForm(true);
    setIsNewVehicleSaved(false);
    setNewVehicleSnapshot(null);
    setEditingVehicleId(null);

    const initialPlate = typeof searchText === "string" ? toPlateFieldValue(searchText) : "";
    reset(
      {
        ...getValues(),
        selectedVehicleId: null,
        ...EMPTY_VEHICLE_VALUES,
        vehiclePlate: initialPlate,
      },
      RESET_OPTIONS
    );
  };

  /** El guardia eligió una fila del buscador: primero hay que avisar la selección (obligatorio en
   * cada selección, no sólo la primera) para que el backend copie al complejo lo que el usuario
   * tenga cargado y habilite leer sus fotos. Recién con eso se hidrata el formulario — la fila del
   * buscador sólo trae patente y marca, no alcanza. */
  const handlePickSearchResult = async (candidate: FindVehicleResult) => {
    if (disabled || selectingVehicle) return;
    setVehicleSelectError(null);
    const result = await selectVehicleApi(candidate.id);
    if (!result.success) {
      setVehicleSelectError(result.error);
      return;
    }
    appendKnownVehicle(result.vehicle);
    handleSelectVehicleChecked(result.vehicle);
  };

  // Con el formulario cerrado y un vehículo ya elegido mostramos su tarjeta en vez del buscador.
  // Un vehículo recién dado de alta ("new") todavía no está en `knownVehicles`, así que sus datos
  // salen del formulario y, si ya se destildó y se limpió, del snapshot que guardó
  // `handleSaveVehicle`.
  const showSelectedVehicleCard = !isDeterministicExit && !!selectedVehicleId && !showAddForm;
  const selectedVehiclePlate =
    selectedVehicle?.plate || newVehiclePlate || newVehicleSnapshot?.vehiclePlate || "";
  const selectedVehicleInfo = [
    selectedVehicle?.brand || newVehicleBrand || newVehicleSnapshot?.vehicleBrand,
    selectedVehicle?.model || newVehicleModel || newVehicleSnapshot?.vehicleModel,
    selectedVehicle?.color || newVehicleColor || newVehicleSnapshot?.vehicleColor,
  ]
    .filter(Boolean)
    .join(" - ");

  // El alta reciente no tiene registro que editar, así que reabrimos el alta con los valores del
  // snapshot: si la tarjeta estaba destildada el formulario está vacío y no alcanza con abrirlo.
  const handleEditNewVehicle = () => {
    if (disabled) return;
    setEditingVehicleId(NEW_VEHICLE_ID);
    setShowAddForm(true);

    reset(
      {
        ...getValues(),
        selectedVehicleId: NEW_VEHICLE_ID,
        ...(newVehicleSnapshot ?? {}),
      },
      RESET_OPTIONS
    );
  };

  const handleEditSelectedVehicle = () => {
    if (disabled) return;
    if (selectedVehicle) handleEditVehicle(selectedVehicle);
    else handleEditNewVehicle();
  };

  const handleRemoveSelectedVehicle = () => {
    if (disabled || !selectedVehicleId) return;
    setIsNewVehicleSaved(false);
    setNewVehicleSnapshot(null);
    handleSelectVehicle(selectedVehicleId, false);
  };

  const handleSaveVehicle = async () => {
    if (disabled) return;
    setSaveError(null);
    // Los obligatorios extra sólo existen en el formulario de transportistas; en el de personas
    // los campos no están montados y validarlos bloquearía el guardado para siempre.
    const fieldsToValidate = ["vehiclePlate", "insuranceExpirationDate"];
    if (isAccordion) {
      fieldsToValidate.push(
        "vehicleTypeId",
        "vehicleBrand",
        "vehicleInsuranceCompany",
        "vehiclePolicyNumber"
      );
      if (appliesVtv) fieldsToValidate.push("vtvExpirationDate");
    }
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      if (editingVehicleId && editingVehicleId !== NEW_VEHICLE_ID) {
        // Los vehículos existentes ya tienen id: sus datos y fotos se persisten ahora, no al
        // enviar el ingreso. Si falla dejamos el formulario abierto con el error para reintentar.
        if (mode === "entry") {
          setSavingVehicle(true);
          const result = await saveExistingVehicle({
            ...(getValues() as EntryRegistrationFormValues),
            selectedVehicleId: editingVehicleId,
          });
          setSavingVehicle(false);
          if (!result.success) {
            setSaveError(result.error || "No se pudo guardar el vehículo.");
            return;
          }
          // Sin esto la tarjeta seguía mostrando los datos previos al guardado: `selectedVehicle`
          // se arma desde `associatedVehicles`, no desde estos campos del formulario.
          if (result.vehicle) appendKnownVehicle(result.vehicle);
        }
        setValue("selectedVehicleId", editingVehicleId);
      } else {
        // Alta nueva o reedición de la misma: guardamos una copia de los datos para la tarjeta.
        setIsNewVehicleSaved(true);
        setNewVehicleSnapshot(pickVehicleValues(getValues()));
        setValue("selectedVehicleId", NEW_VEHICLE_ID);
      }
      setShowAddForm(false);
      setEditingVehicleId(null);
    }
  };

  const handleCancelEdit = () => {
    const wasEditingNewVehicle = editingVehicleId === NEW_VEHICLE_ID;
    setShowAddForm(false);
    setEditingVehicleId(null);

    // Cancelar la edición del alta reciente no puede dejar su tarjeta visible con el formulario en
    // blanco: volvemos a lo último guardado.
    if (wasEditingNewVehicle) {
      reset(
        {
          ...getValues(),
          selectedVehicleId: NEW_VEHICLE_ID,
          ...(newVehicleSnapshot ?? EMPTY_VEHICLE_VALUES),
        },
        RESET_OPTIONS
      );
      return;
    }

    reset(
      {
        ...getValues(),
        vehiclePlate: "",
        vehicleBrand: "",
        vehicleColor: "",
        vehicleModel: "",
        insuranceExpirationDate: null,
        insurancePhoto: null,
        vtvExpirationDate: null,
        vtvPhoto: null,
        vehicleTypeId: "",
        vehicleAppliesVtv: true,
        vehicleInsuranceCompany: "",
        vehiclePolicyNumber: "",
      },
      {
        keepErrors: true,
        keepDirty: true,
        keepTouched: true,
        keepIsSubmitted: true,
        keepSubmitCount: true,
      }
    );
  };

  // Si el chofer ya tiene esa patente registrada (en esta sesión, o consultando findVehicles) la
  // seleccionamos —el efecto de hidratación de arriba completa marca/seguro/VTV desde el
  // registro—; si no, abrimos el alta con la patente ya puesta para que complete el resto.
  React.useEffect(() => {
    if (isExitMode || !authPlate) return;
    if (getValues("selectedVehicleId")) return;

    const existing = knownVehicles.find((v: any) => samePlate(v.plate, authPlate));
    if (existing?.id) {
      handleSelectVehicle(existing.id, true, existing);
      return;
    }

    let cancelled = false;
    (async () => {
      const found = await findExactVehicle(authPlate);
      if (cancelled) return;
      if (found) {
        const result = await selectVehicleApi(found.id);
        if (cancelled) return;
        if (result.success) {
          appendKnownVehicle(result.vehicle);
          handleSelectVehicle(result.vehicle.id, true, result.vehicle);
          return;
        }
      }
      setShowAddForm(true);
      if (getValues("vehiclePlate") !== authPlate) {
        setValue("vehiclePlate", authPlate);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPlate, isExitMode, knownVehicles, getValues, setValue]);

  // Destildar la autorización tiene que deshacer lo que impuso el efecto de arriba: si no, la
  // patente (o el vehículo que eligió por ella) queda puesta como si la hubiera cargado el guardia.
  // Sólo se limpia lo que sigue siendo el de la autorización — la patente, lo único que
  // `applyAuthorizationPrefill` llega a bloquear — nunca marca/seguro/VTV/etc., que el guardia
  // cargó a mano y no tienen nada que ver con la autorización.
  const prevAuthPlateRef = React.useRef(authPlate);
  React.useEffect(() => {
    const prevPlate = prevAuthPlateRef.current;
    prevAuthPlateRef.current = authPlate;
    if (!prevPlate || authPlate) return;
    // El alta ya se guardó ("Guardar" en el formulario): es un vehículo confirmado por el
    // guardia, no un prefill a medio completar. Lo que pase después con la autorización no debe
    // volver a tocarlo — si no, cada vez que se destilda/oculta se pierde lo ya cargado.
    if (isNewVehicleSaved) return;

    const selected = getValues("selectedVehicleId");
    const selectedIsFromAuth = knownVehicles.some(
      (v: any) =>
        (v.id === selected || samePlate(v.plate, selected)) && samePlate(v.plate, prevPlate)
    );
    if (selectedIsFromAuth) {
      // Sólo se deselecciona: `EMPTY_VEHICLE_VALUES` no va acá (aunque el comentario de arriba ya
      // avisaba que no debía tocar marca/seguro/VTV/fotos) porque el guardia puede haber estado
      // completando ese vehículo a mano desde que la autorización lo autoseleccionó — nada de eso
      // está persistido todavía (recién se guarda al backend en el submit final), así que pisarlo
      // acá lo pierde para siempre.
      reset({ ...getValues(), selectedVehicleId: null }, RESET_OPTIONS);
      return;
    }

    if (samePlate(getValues("vehiclePlate"), prevPlate)) {
      // Sólo la patente: si el guardia ya tipeó marca/seguro/etc. en esta alta a medio completar,
      // esos datos siguen siendo suyos y no se pierden.
      setValue("vehiclePlate", "");
    }
  }, [authPlate, knownVehicles, getValues, setValue, reset, isNewVehicleSaved]);

  // Keep the validation in sync: valid when a vehicle is selected or a new one saved
  React.useEffect(() => {
    const isValid = !!selectedVehicleId;
    setValue("selectedVehicleId", isValid ? selectedVehicleId : null, {
      shouldValidate: isSubmitted,
    });
  }, [selectedVehicleId, setValue, isSubmitted]);

  const trimmedSearch = (vehicleSearch || "").trim();
  const hasMinSearchLength = trimmedSearch.length >= MIN_PLATE_SEARCH_LENGTH;
  const hasExactMatch =
    hasMinSearchLength && vehicleSearchResults.some((r) => samePlate(r.plate, trimmedSearch));
  const canAddFromSearch = hasMinSearchLength && !hasExactMatch && !vehicleSearchLoading;
  // La patente identifica al registro existente que vino de findVehicles; sólo se edita al dar de
  // alta un vehículo nuevo.
  const isEditingExistingVehicle =
    !!editingVehicleId && editingVehicleId !== NEW_VEHICLE_ID;
  const validateNewVehiclePlate = React.useCallback(
    async (plate: string) => {
      if (isEditingExistingVehicle || !plate?.trim()) return true;

      const existingVehicle = await findExactVehicle(plate);
      return existingVehicle
        ? "Ya existe un vehiculo con esta patente. Seleccionalo desde el buscador."
        : true;
    },
    [findExactVehicle, isEditingExistingVehicle]
  );

  return (
    <div className="vehicleContentSection" data-field-name="selectedVehicleId">
      {isDeterministicExit ? (
        // Egreso con ingreso previo: el vehículo ya se resolvió antes de llegar acá (evento de
        // entrada / getVehicles), no hay buscador ni alta. Sin evento de entrada (ver
        // "Registrar salida sin ingreso previo") esto no aplica y cae al buscador de más abajo,
        // igual que ingreso.
        (knownVehicles.length > 0 || isNewVehicleSaved) && (
          <div className="existingVehiclesList">
            {knownVehicles.map((v) => {
              const isSelected =
                selectedVehicleId === v.id || (v.plate && selectedVehicleId === v.plate);
              return (
                <ExistingVehicleCard
                  key={v.id}
                  plate={isSelected ? newVehiclePlate || v.plate : v.plate}
                  brand={isSelected ? newVehicleBrand || v.brand : v.brand}
                  model={isSelected ? newVehicleModel || v.model : v.model}
                  color={isSelected ? newVehicleColor || v.color : v.color}
                  checked={isSelected}
                  onCheckedChange={(checked) => handleSelectVehicle(v.id, checked)}
                  onEdit={() => handleEditVehicle(v)}
                  disabled={disabled}
                />
              );
            })}
          </div>
        )
      ) : showSelectedVehicleCard ? (
        <VehicleSummaryCard
          plate={selectedVehiclePlate}
          vehicleInfo={selectedVehicleInfo}
          isActionsDisabled={disabled}
          onEdit={handleEditSelectedVehicle}
          onDelete={handleRemoveSelectedVehicle}
        />
      ) : (
        <>
          <EmpresaSearch
            searchValue={vehicleSearch || ""}
            onSearchChange={(value) => setValue("vehicleSearch", value)}
            selectedEmpresa={selectedVehicle}
            onSelect={handlePickSearchResult}
            onClear={() => {
              setValue("vehicleSearch", "");
              if (selectedVehicleId) handleSelectVehicle(selectedVehicleId, false);
            }}
            options={vehicleSearchResults}
            getLabel={(vehicle: FindVehicleResult) =>
              [vehicle.plate, vehicle.brand].filter(Boolean).join(" - ")
            }
            placeholder={t(BundleKey.VEHICLE_SEARCH_PLACEHOLDER)}
            icon={<DirectionsCarIcon fontSize="small" />}
            emptyLabel={
              !hasMinSearchLength
                ? `Ingresá al menos ${MIN_PLATE_SEARCH_LENGTH} caracteres de la patente`
                : "No se encontraron vehículos con esa patente"
            }
            addNewLabel="Agregar vehículo"
            onAddNew={canAddFromSearch ? handleAddVehicle : undefined}
            disabled={disabled || selectingVehicle}
            loading={selectingVehicle}
            filterLocally={false}
          />
          {(vehicleSelectError || vehicleSearchApiError) && (
            <div className="vehicleSaveError">{vehicleSelectError || vehicleSearchApiError}</div>
          )}
        </>
      )}

      {showAddForm && (
        <div className="vehicleAddFormContainer">
          {editingVehicleId && (
            <div style={{ marginBottom: "8px", fontWeight: 600, fontSize: "16px", color: "#333" }}>
              Editando vehículo
            </div>
          )}
          <div className={`vehicleContentGrid${isAccordion ? " vehicleContentGrid--compact" : ""}`}>
            <FormSelect
              name="vehicleTypeId"
              label={isAccordion ? "Tipo de vehículo *" : "Tipo de vehículo"}
              placeholder="-completar-"
              disabled={disabled}
              options={vehicleTypeOptions}
              rules={isAccordion ? { required: "El tipo de vehículo es obligatorio" } : undefined}
              isSearchable
            />
            <FormTextInput
              name="vehiclePlate"
              label={`${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_PLATE)} *`}
              placeholder="-completar-"
              rules={{
                required: "La patente es obligatoria",
                validate: validateNewVehiclePlate,
              }}
              disabled={disabled || isPlateLocked || isEditingExistingVehicle}
              extraType="plate"
            />
            {!isAccordion && (
              <FormTextInput
                name="vehicleColor"
                label="Color"
                placeholder="-completar-"
                disabled={disabled}
                extraType="text"
              />
            )}
            <FormSelect
              name="vehicleBrand"
              label={
                isAccordion
                  ? `${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_BRAND)} *`
                  : t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_BRAND)
              }
              placeholder="-completar-"
              disabled={disabled}
              options={vehicleBrandOptions}
              rules={isAccordion ? { required: "La marca es obligatoria" } : undefined}
              isSearchable
            />
            {!isAccordion && (
              <FormTextInput
                name="vehicleModel"
                label="Modelo"
                placeholder="-completar-"
                disabled={disabled}
                extraType="alfanumeric"
              />
            )}
          </div>

          {/* Fuera de transportistas seguro y VTV siguen mezclados, pero cada uno en su propia
              grilla: así fecha + foto quedan siempre emparejadas en la misma fila, sin depender de
              cuántos campos haya arriba (con Tipo de vehículo la cantidad pasó a ser impar). */}
          {!isAccordion && (
            <div className="vehicleContentGrid">
              <FormDatePicker
                name="insuranceExpirationDate"
                label={`${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_DATE)} *`}
                placeholder="-completar-"
                variant="pl"
                rules={{ required: "La fecha de vencimiento es obligatoria" }}
                disabled={disabled}
              />
              <FormDocumentPreviewForm
                name="insurancePhoto"
                label="Foto de póliza"
                fileName="Foto de póliza."
                compact
                displayVariant="file"
                readOnly={disabled}
              />
            </div>
          )}

          {!isAccordion && (
            <div className="vehicleContentGrid">
              <FormDatePicker
                name="vtvExpirationDate"
                label="Fecha de vencimiento VTV"
                placeholder="-completar-"
                variant="pl"
                disabled={disabled}
              />
              <FormDocumentPreviewForm
                name="vtvPhoto"
                label="Foto de póliza"
                fileName="Foto de póliza."
                compact
                displayVariant="file"
                readOnly={disabled}
              />
            </div>
          )}

          {isAccordion && (
            <>
              <div className="vehicleFieldsSubsection">
                <div className="vehicleFieldsSubsectionHeader">
                  <span className="vehicleFieldsSubsectionTitle">VTV</span>
                  <Controller
                    name="vehicleAppliesVtv"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              // Sin VTV no puede quedar una fecha vieja colgada en el payload.
                              if (!e.target.checked) setValue("vtvExpirationDate", null);
                            }}
                            disabled={disabled}
                          />
                        }
                        label="Aplica VTV"
                      />
                    )}
                  />
                </div>
                {appliesVtv && (
                  <div className="vehicleContentGrid vehicleContentGrid--compact">
                    <FormDatePicker
                      name="vtvExpirationDate"
                      label="Fecha de vencimiento VTV *"
                      placeholder="-completar-"
                      variant="pl"
                      rules={{
                        required: "La fecha de vencimiento de VTV es obligatoria",
                        validate: validateNotExpired,
                      }}
                      disabled={disabled}
                    />
                    <FormDocumentPreviewForm
                      name="vtvPhoto"
                      label="Foto VTV"
                      fileName="Foto VTV."
                      compact
                      displayVariant="file"
                      readOnly={disabled}
                    />
                  </div>
                )}
              </div>

              <div className="vehicleFieldsSubsection">
                <span className="vehicleFieldsSubsectionTitle">Seguro</span>
                <div className="vehicleContentGrid vehicleContentGrid--compact">
                  <FormSelect
                    name="vehicleInsuranceCompany"
                    label="Aseguradora *"
                    placeholder="-completar-"
                    disabled={disabled}
                    options={insuranceCompanyOptions}
                    rules={{ required: "La aseguradora es obligatoria" }}
                    isSearchable
                  />
                  <FormTextInput
                    name="vehiclePolicyNumber"
                    label="Póliza n° *"
                    placeholder="-completar-"
                    rules={{ required: "El número de póliza es obligatorio" }}
                    disabled={disabled}
                    extraType="alfanumericNoSpaces"
                  />
                </div>
                {/* Propia grilla, como en el flujo fuera de transportistas: así fecha + foto quedan
                    siempre emparejadas en la misma fila, sin depender de los dos campos de arriba. */}
                <div className="vehicleContentGrid vehicleContentGrid--compact">
                  <FormDatePicker
                    name="insuranceExpirationDate"
                    label={`${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_DATE)} *`}
                    placeholder="-completar-"
                    variant="pl"
                    rules={{
                      required: "La fecha de vencimiento es obligatoria",
                      validate: validateNotExpired,
                    }}
                    disabled={disabled}
                  />
                  <FormDocumentPreviewForm
                    name="insurancePhoto"
                    label="Foto seguro"
                    fileName="Foto seguro."
                    compact
                    displayVariant="file"
                    readOnly={disabled}
                  />
                </div>
              </div>
            </>
          )}
          {/* Ya estamos dentro de `showAddForm`: exigir además que el chofer tuviera vehículos
              previos dejaba sin botón de guardar justo al que registra el primero. */}
          {!disabled && (
            <div className="vehicleAddFormActions">
              <Button variant="outline" onClick={handleCancelEdit}>
                {t(BundleKey.ACTION_CANCEL)}
              </Button>
              <Button onClick={handleSaveVehicle} loading={savingVehicle} disabled={savingVehicle}>
                {t(BundleKey.ACTION_SAVE)}
              </Button>
            </div>
          )}
          {saveError && <div className="vehicleSaveError">{saveError}</div>}
        </div>
      )}

      <DialogConfirmation
        open={!!plateMismatch}
        onClose={handleCancelPlateMismatch}
        onConfirm={handleConfirmPlateMismatch}
        title="La patente no coincide"
        message={`La autorización seleccionada es para la patente ${authPlate}. El vehículo elegido tiene otra patente. ¿Querés continuar igual?`}
        confirmLabel="Continuar igual"
        cancelLabel="Cancelar"
      />
    </div>
  );
};

export default VehicleContentUserDataForm;
